import { Number } from "components/Number";
import React, { FC, ReactNode, useState } from "react";
import Select, { Options } from "react-select";
import { useField } from "lib/form";
import { DL_FILTER_EVENT, DL_FILTER_ACTION_ON, DL_FILTER_ACTION_OFF } from "helpers/constants";
import { datalayerWrapper } from "lib/analytics/datalayerHelpers";
import { Count } from "./Count";
import { getCustomStyles } from "./customStyles";

const MultiValueRemove = () => null; // overwrite
const IndicatorSeparator = () => null; // overwrite

type OptionType = {
    aantal?: number;
    datalayerValue?: string;
    displayName?: string;
    id?: string;
    label?: string | JSX.Element;
    naam?: string;
    options?: OptionType[];
    value?: string;
};

const convertToSelectOption = (item: OptionType): OptionType => {
    const { aantal, id, naam, displayName } = item;
    const datalayerValue = naam || displayName;

    const displayNameWithLabel = (
        <>
            {displayName}{" "}
            {aantal !== undefined ? (
                <Count>
                    <Number value={aantal} />
                </Count>
            ) : null}
        </>
    );

    return {
        label: displayNameWithLabel,
        displayName,
        datalayerValue,
        id,
        value: id
    };
};

/** When typing in the select box to filter the options this allows the search to be case insensitive */
const filterOptions = (candidate: { data: { displayName?: string } }, input?: string): boolean => {
    if (input) {
        const { displayName = "" } = candidate.data;
        return displayName?.toLowerCase().includes(input.toLowerCase());
    }

    return true;
};

type Props = {
    fieldName: string;
    label: ReactNode;
    disabled?: boolean;
    ariaLabel?: string;
    autoHeight?: boolean;
    isMulti?: boolean;
    noMakeSelected?: string;
    notAvailableMessage?: string;
    options: Record<string, any>[];
    isHomepage?: boolean;
    forceDisabledPlaceholder?: boolean;
};

export const FilterSelect: FC<Props> = ({
    fieldName,
    label,
    options,
    noMakeSelected = null,
    notAvailableMessage = null,
    isMulti = false,
    disabled,
    autoHeight,
    ariaLabel,
    isHomepage,
    forceDisabledPlaceholder
}) => {
    const [value, , onChange] = useField<string[]>(fieldName);
    const [datalayerVals, setDatalayerVals] = useState<any[]>([]);

    /** Data layer stuff in a separate function */
    const doDataLayerStuff = (selectedOptions: any) => {
        let val = [];

        const prevDatalayerVals = datalayerVals;
        let newDatalayerValues: any[] = [];

        if (Array.isArray(selectedOptions)) {
            val = selectedOptions.map((item) => item.value);
            newDatalayerValues = selectedOptions.map((item) => item.datalayerValue);
        } else if (selectedOptions) {
            val = [selectedOptions.value || null];
            newDatalayerValues = [selectedOptions.datalayerValue];
        }

        setDatalayerVals(newDatalayerValues);

        const added = newDatalayerValues.filter((x) => !prevDatalayerVals.includes(x));
        const removed = prevDatalayerVals.filter((x) => !newDatalayerValues.includes(x));

        if (added.length) {
            datalayerWrapper({
                event: DL_FILTER_EVENT,
                filter_action: DL_FILTER_ACTION_ON,
                filter_name: fieldName,
                filter_value: added[0]
            });
        }

        if (removed.length) {
            datalayerWrapper({
                event: DL_FILTER_EVENT,
                filter_action: DL_FILTER_ACTION_OFF,
                filter_name: fieldName,
                filter_value: removed[0]
            });
        }

        return val;
    };

    const onChangeHandler = (selectedOptions: any): any => {
        const val = doDataLayerStuff(selectedOptions);

        onChange(val);
    };

    const getSelectOptions = (items: OptionType[]): Options<Record<string, any>> => {
        const one: Record<string, any>[] = items.map((x) => {
            if (x.options) {
                const opts = x.options.map(convertToSelectOption);
                const section = { ...x, options: opts };

                return section;
            }

            return convertToSelectOption(x);
        });

        return one;
    };

    const optionsFlat = options.map((item) => (item.options ? [...item.options] : item)).flat();

    return (
        <div>
            <Select
                isSearchable
                isMulti={isMulti}
                instanceId={`filter-${fieldName}`}
                placeholder={
                    forceDisabledPlaceholder && disabled && isHomepage
                        ? label
                        : !isHomepage
                        ? label
                        : ""
                }
                isClearable={false}
                closeMenuOnSelect
                components={{ MultiValueRemove, IndicatorSeparator }}
                value={optionsFlat
                    .filter((item) => value.includes(item.id))
                    .map((item) => {
                        // returns an item without the count next to it
                        return {
                            displayName: item.displayName,
                            label: item.displayName,
                            value: item.id,
                            id: item.id,
                            datalayerValue: item.naam
                        };
                    })}
                options={getSelectOptions(options)}
                noOptionsMessage={() => (optionsFlat[0] ? notAvailableMessage : noMakeSelected)}
                onChange={onChangeHandler}
                styles={getCustomStyles(autoHeight, isHomepage)}
                filterOption={filterOptions}
                aria-label={ariaLabel}
                isDisabled={disabled}
            />
        </div>
    );
};
